import { Icon } from '@assembly-js/design-system';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { SidebarThemeContext } from 'src/context';

import {
  getSettingsSearchOptionId,
  SETTINGS_SEARCH_RESULTS_ID,
  useSettingsSearch,
} from './useSettingsSearch';

/** Grouped results for the current query, in place of the sidebar's sections. */
export const SettingsSearchResults = () => {
  const { t } = useTranslation();
  const { sidebarTextColor } = React.useContext(SidebarThemeContext);
  const { query, options, activeIndex, setActiveIndex, select } =
    useSettingsSearch();

  // Focus stays in the field, so the highlighted row has to be brought into
  // view itself or Enter could open a result off-screen.
  React.useEffect(() => {
    document
      .getElementById(getSettingsSearchOptionId(activeIndex))
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (options.length === 0) {
    return (
      <div className="px-[18px] py-3.5 text-[12.5px] text-secondary">
        {t(($) => $.page_settings.search.no_results, { query: query.trim() })}
      </div>
    );
  }

  return (
    <div
      id={SETTINGS_SEARCH_RESULTS_ID}
      role="listbox"
      aria-label={t(($) => $.page_settings.search.results_label)}
      className="flex flex-col gap-0.5 px-2.5 pt-1.5 pb-6"
    >
      {options.map((option, index) => {
        const isPage = option.kind === 'page';
        return (
          <div
            key={option.id}
            id={getSettingsSearchOptionId(index)}
            role="option"
            aria-selected={index === activeIndex}
            // The field drives the keyboard (aria-activedescendant), so options
            // stay out of the tab order.
            tabIndex={-1}
            // The field keeps focus, so a click can't steal it before select runs.
            onMouseDown={(event) => event.preventDefault()}
            onMouseEnter={() => setActiveIndex(index)}
            onClick={() => select(option)}
            style={isPage ? { color: sidebarTextColor } : undefined}
            className={clsx(
              'flex cursor-pointer items-center rounded leading-5.5',
              isPage
                ? 'gap-[9px] px-2 py-1.5 text-[13.5px] font-medium'
                : 'py-[5px] pr-2 pl-8 text-[13px] text-secondary',
              index === activeIndex && 'bg-gray-150',
              index === activeIndex && !isPage && 'text-black-heading',
            )}
          >
            {isPage && (
              <Icon
                icon={option.page.iconName}
                className="h-[15px] w-[15px] shrink-0 text-secondary"
              />
            )}
            <span className="min-w-0 flex-1 truncate">{option.label}</span>
          </div>
        );
      })}
    </div>
  );
};
